import {
  MAX_RETURN_PERIOD_DAYS,
  isValidDayCount
} from '@/domain/return-conditions'
import {
  PbPatchReturnProcessConditionsReq,
  PbPatchReturnProcessConditionsReqReviewAutoCompleteOutcomeEnum as Outcome,
  PbReturnProcessConditions,
  PbReturnProcessConditionsReviewAutoCompleteOutcomeEnum
} from '@itsrever/rever-api'

export type ReviewAutoCompleteMode = 'OFF' | 'MISSING' | 'APPROVED'

export type ReturnConditionsSettings = {
  returnPeriodDays: number | null
  maxDaysToSendPackage: number | null
  randomizeReturnReasonsOrder: boolean
  reviewAutoComplete: ReviewAutoCompleteMode
  // Null while Off: the setting has no days then.
  reviewAutoCompleteDays: number | null
  // Read-only: auto-complete only applies to returns that are reviewed by hand.
  manualReview: boolean
}

export type ReturnConditionsField = keyof ReturnConditionsSettings

export type DayCountField =
  | 'returnPeriodDays'
  | 'maxDaysToSendPackage'
  | 'reviewAutoCompleteDays'

export const returnConditionsFields: ReturnConditionsField[] = [
  'returnPeriodDays',
  'maxDaysToSendPackage',
  'randomizeReturnReasonsOrder',
  'reviewAutoComplete',
  'reviewAutoCompleteDays'
]

const dayCountFields: DayCountField[] = [
  'returnPeriodDays',
  'maxDaysToSendPackage',
  'reviewAutoCompleteDays'
]

export const emptyReturnConditions: ReturnConditionsSettings = {
  returnPeriodDays: null,
  maxDaysToSendPackage: null,
  randomizeReturnReasonsOrder: false,
  reviewAutoComplete: 'OFF',
  reviewAutoCompleteDays: null,
  manualReview: false
}

// Mirrors maxReviewAutoCompleteDays in settings-api.
export const MAX_REVIEW_AUTO_COMPLETE_DAYS = 365

// Only the return window is bounded in settings-api, so bounding the shipping deadline here too
// would refuse values the server accepts.
const maxByField: Partial<Record<DayCountField, number>> = {
  returnPeriodDays: MAX_RETURN_PERIOD_DAYS,
  reviewAutoCompleteDays: MAX_REVIEW_AUTO_COMPLETE_DAYS
}

// A value over the bound needs its own message: telling the merchant to enter something
// greater than zero when they went over the ceiling explains nothing.
export function dayCountErrorCopy(
  field: DayCountField,
  days: number | null
): { key: string; max?: number } {
  const max = maxByField[field]
  if (max !== undefined && days !== null && days > max) {
    return { key: 'return_conditions.days_max_error', max }
  }
  return { key: 'return_conditions.days_error' }
}

export function changedReturnConditionsFields(
  original: ReturnConditionsSettings,
  edited: ReturnConditionsSettings
): ReturnConditionsField[] {
  return returnConditionsFields.filter(
    (field) => edited[field] !== original[field]
  )
}

// Only edited day-count fields are validated: a field the ecommerce never set stays
// unset instead of forcing the merchant to fill it in to save the other one, and the
// toggles have no invalid state.
export function invalidReturnConditionsFields(
  original: ReturnConditionsSettings,
  edited: ReturnConditionsSettings
): DayCountField[] {
  const changed = changedReturnConditionsFields(original, edited)
  return dayCountFields.filter(
    (field) =>
      mustValidate(field, changed, edited) &&
      !isValidDayCount(edited[field], maxByField[field])
  )
}

// Turning auto-complete on needs days even when the days field itself was not touched.
function mustValidate(
  field: DayCountField,
  changed: ReturnConditionsField[],
  edited: ReturnConditionsSettings
): boolean {
  if (field !== 'reviewAutoCompleteDays') return changed.includes(field)
  return (
    edited.reviewAutoComplete !== 'OFF' &&
    (changed.includes(field) || changed.includes('reviewAutoComplete'))
  )
}

export function toReturnConditions(
  conditions: PbReturnProcessConditions
): ReturnConditionsSettings {
  return {
    returnPeriodDays: conditions.return_period_days ?? null,
    maxDaysToSendPackage: conditions.max_days_to_send_package ?? null,
    randomizeReturnReasonsOrder:
      conditions.randomize_return_reasons_order ?? false,
    reviewAutoComplete: autoCompleteMode(conditions),
    reviewAutoCompleteDays: conditions.review_auto_complete_days || null,
    manualReview: conditions.review_flow === 'MANUAL'
  }
}

// 0 days is off whatever the outcome says; an unset outcome behaves as missing.
function autoCompleteMode(
  conditions: PbReturnProcessConditions
): ReviewAutoCompleteMode {
  if (!conditions.review_auto_complete_days) return 'OFF'
  return conditions.review_auto_complete_outcome ===
    PbReturnProcessConditionsReviewAutoCompleteOutcomeEnum.Approved
    ? 'APPROVED'
    : 'MISSING'
}

// The endpoint is a partial update, so it must receive only the edited
// attributes; echoing unchanged values back would reintroduce lost updates.
export function toPatchRequest(
  ecommerceId: string,
  original: ReturnConditionsSettings,
  edited: ReturnConditionsSettings
): PbPatchReturnProcessConditionsReq {
  const request: PbPatchReturnProcessConditionsReq = {
    ecommerce_id: ecommerceId
  }
  const changed = changedReturnConditionsFields(original, edited)

  if (
    changed.includes('returnPeriodDays') &&
    edited.returnPeriodDays !== null
  ) {
    request.return_period_days = edited.returnPeriodDays
  }
  if (
    changed.includes('maxDaysToSendPackage') &&
    edited.maxDaysToSendPackage !== null
  ) {
    request.max_days_to_send_package = edited.maxDaysToSendPackage
  }
  if (changed.includes('randomizeReturnReasonsOrder')) {
    request.randomize_return_reasons_order = edited.randomizeReturnReasonsOrder
  }
  // Mode and days are one setting: Off is stored as 0 days, on as the days plus the outcome.
  if (
    changed.includes('reviewAutoComplete') ||
    changed.includes('reviewAutoCompleteDays')
  ) {
    if (edited.reviewAutoComplete === 'OFF') {
      request.review_auto_complete_days = 0
    } else if (edited.reviewAutoCompleteDays !== null) {
      request.review_auto_complete_days = edited.reviewAutoCompleteDays
      request.review_auto_complete_outcome =
        edited.reviewAutoComplete === 'APPROVED'
          ? Outcome.Approved
          : Outcome.Missing
    }
  }

  return request
}

export function hasAttributesToPatch(
  request: PbPatchReturnProcessConditionsReq
): boolean {
  return (
    request.return_period_days !== undefined ||
    request.max_days_to_send_package !== undefined ||
    request.randomize_return_reasons_order !== undefined ||
    request.review_auto_complete_days !== undefined
  )
}
